import { useRouteState } from '../routeState'
import { useRefreshGuard } from '../refreshGuards'
import { LivePeople, PresenceDot, useLiveRoom } from '../LiveRoom'
import { TrashProjects } from './TrashProjects'
import { createPortal } from 'react-dom'
import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react'
import {
  ArrowRight,
  Bell,
  Bug,
  Check,
  CheckSquare,
  HelpCircle,
  History,
  Lightbulb,
  MessageSquarePlus,
  RefreshCw,
  UserRound,
  X,
} from 'lucide-react'
import { useTasks } from '../TaskProvider'
import { roomRequest } from '../roomApi'
import { guide } from '../helpGuide'
import type {
  ActivityEvent,
  Feedback,
  ProjectSummary,
  RoomUser,
  StoryProject,
  TourId,
  WritingTask,
  RoomNotification,
} from '../types'
import { WorkspaceDialog } from './WorkspaceDialog'
import { GuidedTour } from './GuidedTour'
import { NotificationsPanel } from './NotificationsPanel'
import { useRoomNotifications } from '../useRoomNotifications'
import { ChatWidget } from './ChatWidget'
import { BibleImageViewer } from './BibleImageViewer'

type Tab =
  'tasks' | 'notifications' | 'activity' | 'feedback' | 'help' | 'profile'
const roomSections = [
  ['tasks', CheckSquare, 'Tasks'],
  ['notifications', Bell, 'Notifications'],
  ['activity', History, 'Activity'],
  ['feedback', Lightbulb, 'Feedback'],
  ['help', HelpCircle, 'Help'],
  ['profile', UserRound, 'Profile'],
] as const
type Props = {
  user: RoomUser
  onProfile: (body: unknown) => Promise<void>
  project: StoryProject | null
  projects: ProjectSummary[]
  context: TourId
  onTourSection: (id: TourId) => void
  onTourStep: (id: TourId, index: number | null) => void
}
export function WorkspaceHub({
  user,
  onProfile,
  project,
  projects,
  context,
  onTourSection,
  onTourStep,
}: Props) {
  const [host, setHost] = useState<Element | null>(null)
  const [trashHost, setTrashHost] = useState<Element | null>(null)
  const [tab, setTab] = useRouteState<Tab | null>('room', null, [null, 'notifications', 'activity', 'feedback', 'help', 'profile'])
  const [feedbackFormVersion, setFeedbackFormVersion] = useState(0)
  const [feedback, setFeedback] = useState<Feedback[]>([])
  const [events, setEvents] = useState<ActivityEvent[]>([])
  const [next, setNext] = useState<string | null>(null)
  const [activityProject, setActivityProject] = useRouteState<string>('activity-project', '')
  const [highlightId, setHighlightId] = useRouteState<string>('highlight', '')
  const contentRef = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [tourRoute, setTourRoute] = useRouteState<string | null>('tour', null)
  const [tourId, tourIndex] = tourRoute?.split(':') || []
  const tour = tourId && Object.hasOwn(guide, tourId) && tourId !== 'tasks'
    ? { id: tourId as TourId, index: Math.max(0, Math.min(guide[tourId as TourId].steps.length - 1, Number.isInteger(Number(tourIndex)) ? Number(tourIndex) : 0)) }
    : null
  const setTour = (value: { id: TourId; index: number } | null) => setTourRoute(value ? value.id + ':' + value.index : null)
  const [savingTour, setSavingTour] = useState(false)
  const [localDismissal, setLocalDismissal] = useState(false)
  const [helpSection, setHelpSection] = useRouteState<TourId>('guide', context, ['library', 'writing', 'map', 'bible', 'tasks', 'activity', 'feedback'])
  const [nickname, setNickname] = useState(
    user.displayName === user.username ? '' : user.displayName,
  )
  const [resumeEnabled, setResumeEnabled] = useState(!!user.preferences.resumeLastView)
  useRefreshGuard(busy || savingTour || (tab === 'profile' && (nickname.trim() || user.username) !== user.displayName), 'Save your profile or finish the current change before refreshing.')
  useEffect(() => { setResumeEnabled(!!user.preferences.resumeLastView) }, [user.preferences.resumeLastView])
  const liveRoom = useLiveRoom()
  const taskStore = useTasks()
  const inbox = useRoomNotifications(user.id)
  const workspaceRequestVersion = useRef(0)
  const request = <T,>(path: string, method?: string, body?: unknown) =>
    roomRequest<T>(user.id, path, method, body)
  useLayoutEffect(() => {
    const target = document.querySelector('[data-room-tools]')
    if (target !== host) setHost(target)
    const trashTarget = document.querySelector('[data-project-trash]')
    if (trashTarget !== trashHost) setTrashHost(trashTarget)
  })
  const loadActivity = async (append = false) => {
    const query = new URLSearchParams({
      ...(activityProject ? { projectId: activityProject } : {}),
      ...(append && next ? { after: next } : {}),
    })
    const result = await request<{
      events: ActivityEvent[]
      next: string | null
    }>('/api/activity?' + query)
    setEvents((old) =>
      append
        ? [
            ...old,
            ...result.events.filter((e) => !old.some((o) => o.id === e.id)),
          ]
        : result.events,
    )
    setNext(result.next)
  }
  const refresh = async () => {
    const version = ++workspaceRequestVersion.current
    setLoading(true)
    setError('')
    try {
      const data = await request<{ feedback: Feedback[] }>('/api/workspace')
      if (version !== workspaceRequestVersion.current) return
      setFeedback(data.feedback)
      if (tab === 'activity') await loadActivity()
    } catch (error) {
      if (version !== workspaceRequestVersion.current) return
      setError(
        error instanceof Error
          ? error.message
          : 'The writing room is unavailable.',
      )
    } finally {
      if (version === workspaceRequestVersion.current) setLoading(false)
    }
  }
  useEffect(() => {
    if (tab && tab !== 'help' && tab !== 'profile' && tab !== 'notifications')
      void refresh()
  }, [tab, activityProject])
  useEffect(() => { const fn = () => { if (tab === 'activity' || tab === 'feedback') void refresh() }; window.addEventListener('bramble:remote-change', fn); window.addEventListener('bramble:writing-changed', fn); return () => { window.removeEventListener('bramble:remote-change', fn); window.removeEventListener('bramble:writing-changed', fn) } }, [tab, activityProject])
  useLayoutEffect(() => {
    contentRef.current?.scrollTo({ top: 0 })
  }, [tab])
  useEffect(() => {
    if (!highlightId || loading) return
    const target = document.getElementById(highlightId)
    if (target) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' })
      target.focus({ preventScroll: true })
    }
  }, [highlightId, tab, loading])
  const act = async (operation: () => Promise<void>) => {
    ++workspaceRequestVersion.current
    setLoading(false)
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await operation()
      ++workspaceRequestVersion.current
      setLoading(false)
      void inbox.refresh()
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'This change could not be saved.',
      )
    } finally {
      setBusy(false)
    }
  }
  const open = (value: Tab) => {
    if (value === 'tasks') { setTab(null); taskStore.openPage(); return }
    setTab(value)
    setError('')
    setNotice('')
    setHighlightId('')
    if (value === 'notifications') void inbox.refresh()
    if (value === 'help') setHelpSection(context)
  }
  const openNotification = async (item: RoomNotification) => {
    const data = await request<{ tasks: WritingTask[]; feedback: Feedback[] }>('/api/workspace')
    if (item.taskId) {
      const task = data.tasks.find((task) => task.id === item.taskId)
      if (!task) throw new Error('This task is no longer available.')
      if (!item.readAt && !(await inbox.markRead(item.id))) return
      await taskStore.refresh()
      setTab(null)
      taskStore.openPage({ taskId: task.id })
    } else if (item.feedbackId) {
      if (!data.feedback.some((feedback) => feedback.id === item.feedbackId))
        throw new Error('This feedback is no longer available.')
      if (!item.readAt && !(await inbox.markRead(item.id))) return
      setFeedback(data.feedback)
      open('feedback')
      setHighlightId('feedback-' + item.feedbackId)
    }
  }
  const startTour = (id: TourId) => {
    if (id === 'tasks') { setTab(null); taskStore.openPage({ tour: true }); return }
    setError('')
    setNotice('')
    setTour({ id, index: 0 })
    if (['tasks', 'activity', 'feedback'].includes(id)) setTab(id as Tab)
    else {
      setTab(null)
      onTourSection(id)
    }
  }
  const finishTour = async (
    status: 'completed' | 'dismissed',
    hide = false,
  ) => {
    if (!tour) return
    setSavingTour(true)
    setError('')
    try {
      await onProfile({
        tour: { id: tour.id, status },
        ...(hide ? { hideTourInvitations: true } : {}),
      })
      setTour(null)
      return true
    } catch (error) {
      setError(
        error instanceof Error
          ? error.message
          : 'Could not save your tour progress.',
      )
      return false
    } finally {
      setSavingTour(false)
    }
  }
  useEffect(() => {
    if (!tour) return
    onTourStep(tour.id, tour.index)
    return () => onTourStep(tour.id, null)
  }, [tour?.id, tour?.index, onTourStep])
  const taskBadgeLabel = inbox.loaded
    ? `${inbox.summary.open} unfinished tasks assigned to you; ${inbox.summary.today} due today; ${inbox.summary.overdue} overdue`
    : 'Checking your tasks'
  const toolbar = (
    <div className="room-toolbar" aria-label="Writing room tools"><LivePeople />
      <button
        type="button"
        onClick={() => open('tasks')}
        title={
          inbox.error
            ? 'Task counts may be out of date. Open notifications to retry.'
            : taskBadgeLabel
        }
        aria-label="Tasks"
      >
        <CheckSquare size={16} />
        <span className="room-button-label">Tasks</span>
        {inbox.loaded && inbox.summary.open > 0 && (
          <strong
            className={
              'room-count' +
              (inbox.summary.overdue
                ? ' room-count--overdue'
                : inbox.summary.today
                  ? ' room-count--today'
                  : '')
            }
            aria-label={taskBadgeLabel}
          >
            {inbox.summary.open > 99 ? '99+' : inbox.summary.open}
          </strong>
        )}
      </button>
      <button
        type="button"
        onClick={() => open('notifications')}
        className="notification-bell"
        title={
          inbox.error
            ? 'Updates could not be refreshed'
            : inbox.loaded
              ? `${inbox.unreadCount} unread notifications`
              : 'Checking notifications'
        }
        aria-label={
          inbox.loaded
            ? `Notifications, ${inbox.unreadCount} unread`
            : 'Notifications'
        }
      >
        <Bell size={16} />
        {inbox.unreadCount > 0 && (
          <strong className="room-count" aria-hidden="true">
            {inbox.unreadCount > 99 ? '99+' : inbox.unreadCount}
          </strong>
        )}
        {inbox.error && (
          <strong
            className="notification-offline"
            aria-label="Updates unavailable"
          >
            !
          </strong>
        )}
      </button>
      <button
        type="button"
        onClick={() => open('feedback')}
        title="Request a feature or report a bug"
        aria-label="Send feedback"
      >
        <MessageSquarePlus size={16} />
      </button>
      <button
        type="button"
        onClick={() => open('help')}
        title="Guides & tours"
        aria-label="Help and tours"
      >
        <HelpCircle size={16} />
      </button>
      <button
        type="button"
        className="room-avatar"
        title={user.displayName + ' · Your profile'}
        aria-label="Your profile"
        onClick={() => open('profile')}
      >
        {user.displayName.slice(0, 1).toUpperCase()}<i className="profile-presence" data-active={liveRoom.connected} />
      </button>
    </div>
  )
  const close = () => {
    if (tour) {
      void finishTour('dismissed').then(saved => { if (saved) setTab(null) })
      return
    }
    setTab(null)
    setError('')
  }
  const invited =
    !tab &&
    !tour &&
    !taskStore.page?.tour &&
    !localDismissal &&
    !user.preferences.hideTourInvitations &&
    !user.preferences.tours[context]
  return (
    <>
      {host && createPortal(toolbar, host)}
      {invited && (
        <aside className="tour-invitation" aria-label="Tour invitation">
          <HelpCircle size={20} />
          <div>
            <strong>{guide[context].title}</strong>
            <span>A short guide, whenever you’re ready.</span>
            <button type="button" onClick={() => startTour(context)}>
              Show me around <ArrowRight size={12} />
            </button>
            <button
              type="button"
              onClick={() =>
                void act(async () => {
                  await onProfile({ hideTourInvitations: true })
                  setLocalDismissal(true)
                })
              }
            >
              I’ll use Help later
            </button>
          </div>
          <button
            type="button"
            className="icon-button"
            aria-label="Dismiss tour invitation"
            onClick={() =>
              void act(async () => {
                await onProfile({
                  tour: { id: context, status: 'dismissed' },
                })
                setLocalDismissal(true)
              })
            }
          >
            <X size={14} />
          </button>
          {error && <p role="alert">{error}</p>}
        </aside>
      )}
      {tab && (
        <WorkspaceDialog
          title="Your writing room"
          eyebrow={'SIGNED IN AS ' + user.displayName}
          className="room-dialog"
          initialFocus="close"
          onClose={close}
        >
          <nav className="room-tabs" aria-label="Writing room sections">
            {roomSections.map(([id, Icon, label]) => (
              <button
                type="button"
                key={id}
                aria-label={label}
                aria-pressed={tab === id}
                disabled={!!tour && tour.id !== id}
                onClick={() => open(id)}
              >
                <Icon size={16} />
                {label}
                {id === 'tasks' && inbox.summary.open > 0 && (
                  <strong className="room-tab-count">
                    {inbox.summary.open > 99 ? '99+' : inbox.summary.open}
                  </strong>
                )}
                {id === 'notifications' && inbox.unreadCount > 0 && (
                  <strong className="room-tab-count">
                    {inbox.unreadCount > 99 ? '99+' : inbox.unreadCount}
                  </strong>
                )}
              </button>
            ))}
          </nav>
          <div className="room-section-picker">
            <select
              aria-label="Writing room section"
              value={tab}
              disabled={!!tour}
              onChange={(event) => open(event.target.value as Tab)}
            >
              {roomSections.map(([id, , label]) => (
                <option key={id} value={id}>
                  {label}
                  {id === 'notifications' && inbox.unreadCount
                    ? ` (${inbox.unreadCount} unread)`
                    : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="room-content" ref={contentRef}>
            {error && (
              <div className="room-error" role="alert">
                {error}
                <button
                  type="button"
                  onClick={() => void refresh()}
                  disabled={loading}
                >
                  Refresh list
                </button>
              </div>
            )}
            {notice && (
              <p role="status" className="room-notice">
                {notice}
              </p>
            )}
            {tab === 'notifications' && (
              <NotificationsPanel
                inbox={inbox}
                busy={busy}
                onOpen={(item) => void act(() => openNotification(item))}
              />
            )}
            {tab === 'activity' && (
              <section data-tour="activity">
                <header className="room-section-heading">
                  <div>
                    <h3>The story behind the changes.</h3>
                    <p>
                      Edits made together share one entry. Open it for before
                      and after excerpts; saved drafts remain in History.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Refresh activity"
                    disabled={loading}
                    onClick={() => void refresh()}
                  >
                    <RefreshCw size={15} />
                  </button>
                </header>
                <label className="activity-filter">
                  Story
                  <select
                    value={activityProject}
                    onChange={(e) => setActivityProject(e.target.value)}
                  >
                    <option value="">All activity</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.title}
                      </option>
                    ))}
                  </select>
                </label>
                <ol className="activity-list">
                  {events.map((event) => (
                    <li key={event.id}>
                      <span className="activity-avatar">
                        {event.actor.name.slice(0, 1).toUpperCase()}<PresenceDot userId={event.actor.id} />
                      </span>
                      <div>
                        <p>
                          <strong>{event.actor.name}</strong>
                          <span className="activity-username">
                            @{event.actor.username}
                          </span>{' '}
                          · {event.action.toLowerCase()}
                        </p>
                        <strong>{event.title}</strong>
                        <time dateTime={event.createdAt}>
                          {new Date(event.createdAt).toLocaleString()}
                        </time>
                        {(event.editCount || 0) > 1 && <small>{event.editCount} edits grouped · from {new Date(event.startedAt!).toLocaleTimeString()}</small>}
                        {event.detailsExpired && <small>Older change excerpts have expired. The author and time are kept.</small>}
                        {(event.editCount || 0) > 1 && event.changes?.length === 0 && !event.omittedChanges && <small>Text returned to its starting point during these edits.</small>}
                        {!!event.omittedChanges && <small>{event.omittedChanges} more changed fields. Use History for full saved drafts.</small>}
                        {event.privateTo && (
                          <small>
                            {event.feedbackId
                              ? 'Feedback · author and room owner'
                              : 'Personal · only you'}
                          </small>
                        )}
                        {!!event.changes?.length && (
                          <details>
                            <summary>
                              {event.changes.length} changed{' '}
                              {event.changes.length === 1
                                ? 'field'
                                : 'fields'}
                            </summary>
                            {event.changes.map((change, i) => (
                              <div className="activity-diff" key={i}>
                                <h4>{change.field}</h4>
                                <div>
                                  <label>
                                    {(event.editCount || 0) > 1 ? 'Before first edit' : 'Before'}
                                    <pre>{change.before || '(empty)'}</pre>
                                  </label>
                                  <label>
                                    {(event.editCount || 0) > 1 ? 'After latest edit' : 'After'}
                                    <pre>{change.after || '(empty)'}</pre>
                                  </label>
                                </div>
                                {change.truncated && (
                                  <small>
                                    Excerpt shortened. Use History for full
                                    saved drafts.
                                  </small>
                                )}
                              </div>
                            ))}
                          </details>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
                {loading && <p role="status">Opening activity…</p>}
                {!loading && !events.length && (
                  <p className="empty-message">
                    New changes will appear here. Earlier work has been kept
                    without assigning an author.
                  </p>
                )}
                {next && (
                  <button
                    type="button"
                    className="button"
                    disabled={busy}
                    onClick={() => void act(() => loadActivity(true))}
                  >
                    Load earlier changes
                  </button>
                )}
              </section>
            )}
            {tab === 'feedback' && (
              <section data-tour="feedback">
                <header className="room-section-heading">
                  <div>
                    <h3>All your prayers have an inbox.</h3>
                    <p>
                      Ideas, rough edges, and things that would make writing
                      easier.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Refresh feedback"
                    disabled={loading}
                    onClick={() => void refresh()}
                  >
                    <RefreshCw size={15} />
                  </button>
                </header>
                <FeedbackForm
                  key={feedbackFormVersion}
                  busy={busy}
                  onSubmit={(body) =>
                    act(async () => {
                      const result = await request<{ feedback: Feedback }>(
                        '/api/feedback',
                        'POST',
                        {
                          ...body,
                          context: {
                            projectId: project?.id || '',
                            view: context,
                          },
                        },
                      )
                      setFeedback((old) => [...old, result.feedback])
                      setFeedbackFormVersion((v) => v + 1)
                      setNotice(
                        'Received. You can follow this request below.',
                      )
                    })
                  }
                />
                <h3>
                  {user.role === 'admin' ? 'Room inbox' : 'Your requests'}
                </h3>
                <div className="feedback-list">
                  {[...feedback].reverse().map((item) => (
                    <FeedbackCard
                      key={item.id + ':' + item.version}
                      item={item}
                      owner={taskStore.members.find(m => m.id === item.ownerId)?.displayName || 'Writer'}
                      admin={user.role === 'admin'}
                      canAnswer={user.role === 'admin' && item.ownerId !== user.id}
                      busy={busy}
                      onSave={(status, response, answered) =>
                        void act(async () => {
                          const result = await request<{
                            feedback: Feedback
                          }>('/api/feedback/' + item.id, 'PATCH', {
                            version: item.version,
                            status,
                            response,
                            answered,
                          })
                          setFeedback((old) =>
                            old.map((f) =>
                              f.id === item.id ? result.feedback : f,
                            ),
                          )
                          setNotice(answered ? 'Prayer answered. The requester has a notification with your message.' : 'Request updated.')
                        })
                      }
                    />
                  ))}
                </div>
                {!loading && !feedback.length && (
                  <p className="empty-message">
                    Your first idea can start here.
                  </p>
                )}
              </section>
            )}
            {tab === 'help' && (
              <section className="help-guide">
                <header>
                  <span className="eyebrow">THE BRAMBLE FIELD GUIDE</span>
                  <h3>Get comfortable. Then make something.</h3>
                  <p>
                    Read a section or take a short tour. Tour progress belongs
                    to your account, so it follows you between browsers.
                  </p>
                </header>
                <div className="help-layout">
                  <nav aria-label="Guide chapters">
                    {Object.entries(guide).map(([id, section]) => (
                      <button
                        type="button"
                        key={id}
                        aria-pressed={helpSection === id}
                        onClick={() => setHelpSection(id as TourId)}
                      >
                        {section.title}
                        {user.preferences.tours[id as TourId]?.status ===
                          'completed' && <Check size={13} />}
                      </button>
                    ))}
                  </nav>
                  <article>
                    <h3>{guide[helpSection].title}</h3>
                    <p>{guide[helpSection].intro}</p>
                    {guide[helpSection].steps.map((s, i) => (
                      <section key={s.title}>
                        <h4>
                          <span>{i + 1}</span>
                          {s.title}
                        </h4>
                        <p>{s.text}</p>
                      </section>
                    ))}
                    <button
                      type="button"
                      className="button button--primary"
                      disabled={
                        !project &&
                        ['writing', 'map', 'bible'].includes(helpSection)
                      }
                      onClick={() => startTour(helpSection)}
                    >
                      {user.preferences.tours[helpSection]
                        ? 'Replay tour'
                        : 'Start tour'}
                      <ArrowRight size={14} />
                    </button>
                    {!project &&
                      ['writing', 'map', 'bible'].includes(helpSection) && (
                        <small>
                          Open a story first to tour this section.
                        </small>
                      )}
                  </article>
                </div>
                <label className="help-preference">
                  <input
                    type="checkbox"
                    checked={!!user.preferences.hideTourInvitations}
                    disabled={busy}
                    onChange={(e) =>
                      void act(() =>
                        onProfile({ hideTourInvitations: e.target.checked }),
                      )
                    }
                  />
                  Hide tour invitations. Guides and replay stay available in
                  Help.
                </label>
                <details className="help-recovery">
                  <summary>Saving, conflicts, accounts, and recovery</summary>
                  <p>
                    Your story library is shared. Personal tasks and tour
                    progress belong to your account. Clearing cookies signs
                    you out; signing in with the same username brings them
                    back. Your nickname can change without changing your
                    identity.
                  </p>
                  <p>
                    Autosave runs while you write, and both writers’ text merges live.
                    Green badges show who is here. Hover a cursor label to fade it.
                    Your browser keeps a recovery draft when offline; let it reconnect
                    before closing when possible. Download a backup if a save needs attention.
                  </p>
                  <p>
                    A BRAMBLE backup contains one project and its bible,
                    images, audio, and connections. Room tasks, chat, feedback, activity,
                    and accounts are part of the server backup. Word and
                    runtime exports focus on the manuscript or playable story.
                  </p>
                  <p>
                    Each account has separate browser recovery copies. Old
                    shared-login recovery files are left untouched during the
                    update; export unsaved work before upgrading. If you lose
                    a password, ask the room owner to reset that account on
                    the server.
                  </p>
                </details>
              </section>
            )}
            {tab === 'profile' && (
              <section className="profile-panel">
                <span className="large-avatar">
                  {user.displayName.slice(0, 1).toUpperCase()}<i className="profile-presence" data-active={liveRoom.connected} />
                </span>
                <h3>{user.displayName}</h3>
                <label className="help-preference resume-preference"><input type="checkbox" checked={resumeEnabled} disabled={busy}
                  onChange={e => { const enabled = e.target.checked; setResumeEnabled(enabled); void act(async () => {
                    try { await onProfile({ resumeLastView: enabled }) } catch (error) { setResumeEnabled(!enabled); throw error }
                  }) }} />Resume where I left off</label>
                <p>Opening Bramble returns to your last working view. Shared links open their own destination. This setting belongs to your account.</p>
                <p>
                  @{user.username} ·{' '}
                  {user.role === 'admin' ? 'Room owner' : 'Writer'}
                </p>
                <form
                  onSubmit={(e) => {
                    e.preventDefault()
                    void act(async () => {
                      await onProfile({ displayName: nickname })
                      setNotice('Your nickname is saved.')
                    })
                  }}
                >
                  <label>
                    Nickname (optional)
                    <input
                      value={nickname}
                      maxLength={60}
                      placeholder={user.username}
                      onChange={(e) => setNickname(e.target.value)}
                    />
                  </label>
                  <p>
                    Leave this blank to use your username. Your account,
                    tasks, and previous changes keep the same identity.
                  </p>
                  <button
                    type="submit"
                    className="button button--primary"
                    disabled={busy}
                  >
                    Save nickname
                  </button>
                </form>
                <p>
                  To change your password, ask the room owner. Password
                  changes are applied to one account at a time.
                </p>
              </section>
            )}
          </div>
        </WorkspaceDialog>
      )}
      <ChatWidget host={host} />
      <TrashProjects host={trashHost} />
      <BibleImageViewer project={project} />
      {tour && <GuidedTour title={guide[tour.id].title} steps={guide[tour.id].steps} index={tour.index}
        busy={savingTour} error={error} onStep={index => { setError(''); setTour({ ...tour, index }) }}
        onFinish={status => void finishTour(status)}
        onLeave={() => { setTour(null); setError(''); setLocalDismissal(true) }} />}

    </>
  )
}

function FeedbackForm({
  busy,
  onSubmit,
}: {
  busy: boolean
  onSubmit: (body: {
    type: 'feature' | 'bug'
    title: string
    body: string
  }) => Promise<void>
}) {
  const [type, setType] = useRouteState<'feature' | 'bug'>('feedback-type', 'feature', ['feature', 'bug']),
    [title, setTitle] = useState(''),
    [body, setBody] = useState('')
  useRefreshGuard(busy || !!title.trim() || !!body.trim(), 'Finish or clear your feedback draft before refreshing.')
  const submit = async (event: FormEvent) => {
    event.preventDefault()
    await onSubmit({ type, title, body })
  }
  return (
    <form
      className="feedback-form"
      data-tour="feedback-form"
      onSubmit={submit}
    >
      <div className="segmented-control">
        <button
          type="button"
          aria-pressed={type === 'feature'}
          onClick={() => setType('feature')}
        >
          <Lightbulb size={14} />
          Feature request
          <Check size={14} className="selection-check" aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-pressed={type === 'bug'}
          onClick={() => setType('bug')}
        >
          <Bug size={14} />
          Report a bug
          <Check size={14} className="selection-check" aria-hidden="true" />
        </button>
      </div>
      <label>
        {type === 'bug' ? 'What went wrong?' : 'What would you love to do?'}
        <input
          aria-label="Feedback title"
          value={title}
          maxLength={200}
          required
          onChange={(e) => setTitle(e.target.value)}
          placeholder={
            type === 'bug'
              ? 'A short description of the problem'
              : 'Even the wild ideas are welcome'
          }
        />
      </label>
      <label>
        Tell us a little more
        <textarea
          value={body}
          maxLength={10000}
          rows={4}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            type === 'bug'
              ? 'What were you doing? What happened, and what did you expect?'
              : 'How would this help you write? An example is always useful.'
          }
        />
      </label>
      <button
        type="submit"
        className="button button--primary"
        disabled={busy || !title.trim()}
      >
        <MessageSquarePlus size={14} />
        {busy
          ? 'Saving…'
          : type === 'bug'
            ? 'Send bug report'
            : 'Send feature request'}
      </button>
    </form>
  )
}
function FeedbackCard({
  item,
  owner,
  admin,
  canAnswer,
  busy,
  onSave,
}: {
  item: Feedback
  owner: string
  admin: boolean
  canAnswer: boolean
  busy: boolean
  onSave: (status: string, response: string, answered?: boolean) => void
}) {
  const [answering, setAnswering] = useState(false)
  const [status, setStatus] = useState(item.status),
    [response, setResponse] = useState(item.response)
  useRefreshGuard(answering || status !== item.status || response !== item.response, 'Save or cancel your feedback reply before refreshing.')
  return (
    <article
      className="feedback-card"
      id={'feedback-' + item.id}
      tabIndex={-1}
    >
      <header>
        <span className="feedback-type">
          {item.type === 'bug' ? 'BUG REPORT' : 'FEATURE REQUEST'}
        </span>
        <span className={'feedback-status feedback-status--' + item.status}>
          {item.answeredAt && item.status === 'done' ? 'Prayer answered' : item.status.replace('-', ' ')}
        </span>
      </header>
      <h4>{item.title}</h4>
      <p>{item.body}</p>
      <small>
        {owner} · {new Date(item.createdAt).toLocaleDateString()}
        {item.context?.view ? ' · ' + item.context.view : ''}
      </small>
      {item.response && (
        <blockquote>
          <strong>{item.answeredAt ? 'Prayer answered · ' : 'From '}{item.responseBy?.name || 'the room owner'}</strong>
          <p>{item.response}</p>
        </blockquote>
      )}
      {canAnswer && !item.answeredAt && <div className="prayer-answer">
        {!answering ? <button type="button" className="button button--primary" disabled={busy} onClick={() => { setResponse(''); setAnswering(true) }}><Check size={14} />Prayer answered</button> :
          <form onSubmit={e => { e.preventDefault(); onSave('done', response, true) }}>
            <label>A short message for {owner}<textarea aria-label="Prayer answered message" value={response} maxLength={500} rows={3} required
              placeholder="What’s ready, and where can they try it?" onChange={e => setResponse(e.target.value)} /></label>
            <div className="prayer-answer-actions"><button type="submit" className="button button--primary" disabled={busy || !response.trim()}>Answer & notify</button>
              <button type="button" className="button" disabled={busy} onClick={() => { setAnswering(false); setResponse(item.response) }}>Cancel</button></div>
          </form>}
      </div>}
      {admin && (
        <details>
          <summary>Update status or reply</summary>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSave(status, response)
            }}
          >
            <label>
              Status
              <select
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as Feedback['status'])
                }
              >
                {['open', 'planned', 'in-progress', 'done', 'closed'].map(
                  (s) => (
                    <option key={s} value={s}>
                      {s.replace('-', ' ')}
                    </option>
                  ),
                )}
              </select>
            </label>
            <label>
              Reply
              <textarea
                value={response}
                rows={3}
                maxLength={10000}
                onChange={(e) => setResponse(e.target.value)}
              />
            </label>
            <button type="submit" className="button" disabled={busy}>
              Save update
            </button>
          </form>
        </details>
      )}
    </article>
  )
}
